import React, { useState, useEffect } from 'react';
import './SurfConditionsDashboard.css';

const SurfConditionsDashboard = () => {
  const [conditions, setConditions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchCurrentConditions();
    // Refresh every 5 minutes
    const interval = setInterval(fetchCurrentConditions, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchCurrentConditions = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/public/surf/current', { cache: 'no-store' });
      const data = await response.json();
      
      debugLog('fetchCurrentConditions: response', {
        hasError: !!data.error,
        conditionRating: data?.conditionRating,
        surfHeight: data?.surfHeight,
      });

      if (data.error) {
        setError(data.message || 'Failed to fetch surf conditions');
      } else {
        setConditions(data);
        setLastUpdated(new Date());
        setError(null);
      }
    } catch (err) {
      setError('Network error fetching surf conditions');
      console.error('Error fetching surf conditions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Debug logger for rating decisions
  const debugLog = (...args) => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[SurfRating]', ...args);
    }
  };

  // Normalize Surfline rating strings to Surfline's 5-step scale
  // Very Poor: 1, Poor: 2, Poor to Fair: 3, Fair: 4, Fair to Good: 5
  // Good and Epic override to full bars (5)
  const getRatingLevel = (rating) => {
    if (!rating) return null;
    const upper = String(rating).toUpperCase().trim();

    let level = null;
    if (/EPIC/.test(upper)) level = 5; // override full
    else if (/FAIR\s*TO\s*GOOD/.test(upper)) level = 5; // check compound before singles
    else if (/GOOD|EXCELLENT/.test(upper)) level = 5; // Good/Excellent override
    else if (/POOR\s*TO\s*FAIR/.test(upper)) level = 3; // check compound before FAIR/POOR
    else if (/VERY\s+POOR/.test(upper)) level = 1; // before POOR
    else if (/\bFAIR\b/.test(upper)) level = 4;
    else if (/\bPOOR\b/.test(upper)) level = 2;
    else if (/FLAT/.test(upper)) level = 0;

    debugLog('getRatingLevel', { rating: upper, level });
    return level;
  };

  const getConditionColor = (rating) => {
    const level = getRatingLevel(rating);
    const css = (name, fallback) => getComputedStyle(document.documentElement).getPropertyValue(name) || fallback;
    const upper = String(rating || '').toUpperCase();
    let color = css('--gray-600', '#6C757D');

    // Special colors for overrides
    if (/EPIC/.test(upper)) {
      color = css('--accent-color', '#9C27B0'); // purple for Epic
    } else if (/GOOD|EXCELLENT/.test(upper)) {
      color = css('--pink-color', '#005a0f'); // pink for Good/Excellent
    } else if (level === null || level <= 0) {
      color = css('--gray-600', '#6C757D'); // FLAT or unknown
    } else if (level === 1) {
      color = css('--gray-600', '#ff0000'); // Very Poor
    } else if (level === 2) {
      color = css('--warning-dark', '#FF6900'); // Poor
    } else if (level === 3) {
      color = css('--warning-color', '#FFB900'); // Poor to Fair
    } else if (level === 4) {
      color = css('--success-color', '#2E8B57'); // Fair
    } else {
      color = css('--success-color', '#00C851'); // Fair to Good (5)
    }
    debugLog('getConditionColor', { rating, level, color });
    return color;
  };

  const getConditionBars = (rating) => {
    // Surfline scale is 1..5 (0 for FLAT). Bars should equal the level value.
    const totalBars = 5;
    const level = getRatingLevel(rating);
    // Clamp to 0..5 and ensure integer
    const activeBars = Math.max(0, Math.min(totalBars, Number.isFinite(level) ? Math.round(level) : 0));
    debugLog('getConditionBars', { rating, level, activeBars });

    return Array.from({ length: totalBars }, (_, i) => (
      <div
        key={i}
        className={`condition-bar ${i < activeBars ? 'active' : ''}`}
        style={{ backgroundColor: i < activeBars ? getConditionColor(rating) : (getComputedStyle(document.documentElement).getPropertyValue('--gray-300') || '#E0E0E0') }}
      />
    ));
  };

  const getSurfHeightText = () => {
    const min = conditions?.surfHeight?.min;
    const max = conditions?.surfHeight?.max;
    const desc = conditions?.surfHeight?.description || '';

    // Prefer numeric values when present (allow 0)
    const hasMin = min !== null && min !== undefined;
    const hasMax = max !== null && max !== undefined;
    if (hasMin && hasMax) {
      return `${min}-${max}ft`;
    }

    // Try to parse ranges like "2-3", "2 - 3 ft", or "2 to 3"
    if (desc) {
      const rangeDash = desc.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/i);
      const rangeTo = desc.match(/(\d+(?:\.\d+)?)\s*(?:to|TO)\s*(\d+(?:\.\d+)?)/i);
      const match = rangeDash || rangeTo;
      if (match) {
        return `${match[1]}-${match[2]}ft`;
      }
      // If unparseable, show description directly
      return desc;
    }

    return 'N/A';
  };

  const formatSwellDirection = (direction, angle) => {
    if (angle) {
      return `${direction} ${angle}°`;
    }
    return direction;
  };

  const getWindDirectionArrow = (direction) => {
    const directions = {
      'N': '↑', 'NNE': '↗', 'NE': '↗', 'ENE': '↗',
      'E': '→', 'ESE': '↘', 'SE': '↘', 'SSE': '↘',
      'S': '↓', 'SSW': '↙', 'SW': '↙', 'WSW': '↙',
      'W': '←', 'WNW': '↖', 'NW': '↖', 'NNW': '↖'
    };
    return directions[direction] || '○';
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString;
  };

  if (loading && !conditions) {
    return (
      <div className="surf-dashboard loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading surf conditions...</p>
        </div>
      </div>
    );
  }

  if (error && !conditions) {
    return (
      <div className="surf-dashboard error">
        <div className="error-message">
          <h3>⚠️ Unable to load surf conditions</h3>
          <p>{error}</p>
          <button onClick={fetchCurrentConditions} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="surf-dashboard">
      <div className="dashboard-header">
        <h2>🏄‍♂️ Current Surf Conditions</h2>
        <div className="location-info">
          <span className="location">{conditions?.location || (process.env.REACT_APP_LOCATION_NAME || 'Beach, ST')}</span>
          {lastUpdated && (
            <span className="last-updated">
              Updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      <div className="conditions-grid">
        {/* Condition Rating */}
        <div className="condition-card rating-card">
          <div className="card-header">
            <h3>CONDITION RATING</h3>
          </div>
          <div className="rating-content">
            <div className="rating-value" style={{ color: getConditionColor(conditions?.conditionRating) }}>
              {conditions?.conditionRating || 'N/A'}
            </div>
            <div className="rating-bars">
              {getConditionBars(conditions?.conditionRating)}
            </div>
          </div>
        </div>

        {/* Surf Height */}
        <div className="condition-card surf-height-card">
          <div className="card-header">
            <h3>SURF HEIGHT</h3>
          </div>
          <div className="surf-height-content">
            <div className="height-display">
              <span className="height-range">{getSurfHeightText()}</span>
            </div>
            {conditions?.surfHeight?.description && (
              <div className="height-description">
                {conditions.surfHeight.description}
              </div>
            )}
          </div>
        </div>

        {/* Swell */}
        <div className="condition-card swell-card">
          <div className="card-header">
            <h3>SWELL</h3>
          </div>
          <div className="swell-content">
            {conditions?.swells && conditions.swells.length > 0 ? (
              conditions.swells.map((swell, index) => (
                <div key={index} className="swell-reading">
                  <div className="swell-height">{swell.height}ft</div>
                  <div className="swell-period">{swell.period}s</div>
                  <div className="swell-direction">
                    <span className="direction-arrow">◄</span>
                    <span>{formatSwellDirection(swell.direction, swell.angle)}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-swell">No swell data available</div>
            )}
          </div>
        </div>

        {/* Wind */}
        <div className="condition-card wind-card">
          <div className="card-header">
            <h3>WIND</h3>
          </div>
          <div className="wind-content">
            <div className="wind-main">
              <div className="wind-speed-direction">
                {conditions?.wind?.speed ? (
                  <>
                    <span className="wind-speed">{conditions.wind.speed}kts</span>
                    <span className="wind-direction">
                      {conditions.wind.direction || 'N/A'}
                      <span className="wind-arrow">
                        {getWindDirectionArrow(conditions.wind.direction)}
                      </span>
                    </span>
                  </>
                ) : (
                  <span>No wind data</span>
                )}
              </div>
              <div className="wind-compass">
                <div className="compass-circle">
                  <div className="compass-arrow" style={{
                    transform: `rotate(${getWindAngle(conditions?.wind?.direction)}deg)`
                  }}>
                    ↑
                  </div>
                </div>
              </div>
            </div>
            <div className="wind-details">
              {conditions?.wind?.gusts && (
                <div className="wind-gusts">{conditions.wind.gusts}kts gusts</div>
              )}
              {conditions?.wind?.description && (
                <div className="wind-description">{conditions.wind.description}</div>
              )}
            </div>
          </div>
        </div>

        {/* Tide */}
        <div className="condition-card tide-card">
          <div className="card-header">
            <h3>TIDE</h3>
          </div>
          <div className="tide-content">
            <div className="upcoming-tides">
              {conditions?.tide?.upcoming && conditions.tide.upcoming.length > 0 ? (
                <>
                  <div className="upcoming-header">Next 3 Tides:</div>
                  {conditions.tide.upcoming.slice(0, 3).map((tide, index) => (
                    <div key={index} className="upcoming-tide">
                      <span className={`tide-type ${tide.type === 'H' ? 'high' : 'low'}`}>
                        {tide.type === 'H' ? '🔺' : '🔻'} {tide.height?.toFixed(2)}ft
                      </span>
                      <span className="tide-time">
                        {tide.time ? new Date(tide.time).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        }) : 'N/A'}
                      </span>
                    </div>
                  ))}
                </>
              ) : (
                <div className="no-upcoming">No upcoming tide data</div>
              )}
            </div>
          </div>
        </div>

        {/* Temperature */}
        <div className="condition-card temperature-card">
          <div className="card-header">
            <h3>TEMPERATURE</h3>
          </div>
          <div className="temperature-content">
            <div className="temp-readings">
              {conditions?.temperature?.air && (
                <div className="air-temp">
                  <span className="temp-icon">🌡️</span>
                  <span className="temp-value">{conditions.temperature.air}°f</span>
                </div>
              )}
              {conditions?.temperature?.water && (
                <div className="water-temp">
                  <span className="temp-icon">🌊</span>
                  <span className="temp-value">{conditions.temperature.water}°f</span>
                </div>
              )}
            </div>
            {conditions?.temperature?.wetsuitRecommendation && (
              <div className="wetsuit-recommendation">
                {conditions.temperature.wetsuitRecommendation}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-footer">
        <div className="refresh-controls">
          <button 
            onClick={fetchCurrentConditions} 
            className="refresh-button"
            disabled={loading}
          >
            {loading ? '⟳' : '🔄'} Refresh
          </button>
          {conditions?.cached && (
            <span className="cache-indicator">📦 Cached data</span>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to get wind angle for compass
const getWindAngle = (direction) => {
  const angles = {
    'N': 0, 'NNE': 22.5, 'NE': 45, 'ENE': 67.5,
    'E': 90, 'ESE': 112.5, 'SE': 135, 'SSE': 157.5,
    'S': 180, 'SSW': 202.5, 'SW': 225, 'WSW': 247.5,
    'W': 270, 'WNW': 292.5, 'NW': 315, 'NNW': 337.5
  };
  return angles[direction] || 0;
};

export default SurfConditionsDashboard; 